import React, { useState } from 'react';

import { nanoid } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getPasswordValidationError } from '@utils';
import { usePasswordValidation, useNotifications, useNavigate } from '@utils/hooks';

import { globalActions, userActions, useStore } from '@modules';
import { VerifyNextEnum } from '@modules/types';

import Button from '@components/Button';
import Captcha from '@components/Captcha';
import Input from '@components/Input';
import MainLayout from '@layouts/MainLayout';

import style from './ChangePassword.scss';

const cn = bemCssModules(style, 'ChangePassword');

const { sendEmail } = VerifyNextEnum;

const ChangePassword: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { showEqualPasswordsNotification } = useNotifications();
  const validationErrors = usePasswordValidation(password);

  const [{ user, global }, dispatch] = useStore();
  const { isShowCaptcha, captcha } = global;
  const { otpKey } = user;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isShowCaptcha) dispatch(globalActions.setShowCaptcha(false));

    const validationError = getPasswordValidationError(password);

    if (validationError) return setError(t(validationError));

    return dispatch(userActions.changeNewPassword({ new_password: password, captcha, key: otpKey }))
      .unwrap()
      .then(res => {
        if (!user.data?.has_password) {
          dispatch(userActions.getUserData());
          navigate(CONST.nav.profileEdit);

          return;
        }
        if (res.data.next === sendEmail) {
          dispatch(userActions.changePasswordSendEmail({ password, captcha }))
            .unwrap()
            .then(({ data }) => {
              if (data.next) {
                dispatch(userActions.clearErrors());
                const flow =
                  data.next === VerifyNextEnum.verifyEmail
                    ? 'changePasswordVerifyEmail'
                    : 'changePasswordVerifyPhone';
                navigate<'verifications'>(CONST.nav.verification, {
                  state: {
                    nextPage: CONST.nav.profileEdit,
                    flow,
                    password,
                  },
                });
              }
            })
            .catch(err => err);
        } else if (res.data.token) {
          navigate(CONST.nav.profile);
        }
      })
      .catch(err => {
        const message = err.data.new_password[0];
        showEqualPasswordsNotification(message);
      });
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const isLoading =
    user.loaders.changePassword ||
    user.loaders.changePasswordSendEmail ||
    user.loaders.changeNewPassword;
  const isDisabled = !!validationErrors.filter(elem => !elem.isValid).length;

  const onBack = () => navigate(CONST.nav.profileEdit);

  return (
    <MainLayout className={cn(null, [className])} backButton={onBack}>
      <div className={cn('content')}>
        <h1 className={cn('title')}>{t('account.change_password')}</h1>
        <form onSubmit={handleSubmit} className={cn('form')}>
          <Input
            type="password"
            placeholder={t('public.password_placeholder')}
            autoFocus
            autoComplete="new-password"
            onChange={handleChangePassword}
            isLoading={isLoading}
            error={error}
          />
          <div className={cn('validate')}>
            {validationErrors.map(({ isValid, text }) => (
              <div key={nanoid()} className={cn('validation-error', { isValid })}>
                {text}
              </div>
            ))}
          </div>
          {isShowCaptcha && <Captcha className={cn('captcha')} />}

          <Button
            className={cn('button')}
            type="submit"
            disabled={isDisabled}
            isLoading={isLoading}
          >
            {t('set_password.save')}
          </Button>
        </form>
      </div>
    </MainLayout>
  );
};

export default ChangePassword;
